import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, Download, X } from 'lucide-react'
import apiClient from '@/api/client'
import type { StockCategory, StockMovement } from '@/types'
import { useCurrency } from '@/contexts/CurrencyContext'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DatePicker } from '@/components/ui/date-picker'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { PaginationControlsComponent } from '@/components/ui/pagination-controls'
import { usePagination } from '@/hooks/usePagination'
import { formatDateTimeHuman } from '@/lib/utils'
import { fmtQty, fmtQtyUnit, isNearIntegerArtifact, QTY_ARTIFACT_HINT } from '@/lib/quantity'
import { describeMovement, MOVEMENT_TONE_CLS } from './describeMovement'

/**
 * History (formerly "Movements", F-FE-N104): the stock ledger in plain
 * language. Sales are grouped one row per order (a 3-ingredient dish is
 * "Sold · Order #N · 3 items", expandable — not three near-identical rows);
 * every row says what happened, who did it, and who voided it; voided rows
 * are hidden by default; the pager shows the real total with a page-size
 * selector; and the whole range exports to Excel.
 */

// Grouped type choices — the vocabulary an operator thinks in, mapped to the
// movement types the API validates.
const TYPE_GROUPS: { value: string; label: string; types: string }[] = [
  { value: '', label: 'Everything', types: '' },
  { value: 'sales', label: 'Sales & refunds', types: 'sale,sale_reversal' },
  { value: 'received', label: 'Purchases received', types: 'purchase' },
  { value: 'adjustments', label: 'Counts & adjustments', types: 'adjustment' },
  { value: 'issued', label: 'Issued & waste', types: 'issue' },
  { value: 'returns', label: 'Vendor returns', types: 'supplier_return' },
  { value: 'opening', label: 'Opening balances', types: 'opening_balance' },
]

function isoDaysAgo(days: number): string {
  const d = new Date(Date.now() - days * 86_400_000)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function InventoryHistoryTab({ categories, showToast, itemFilter, onClearItemFilter }: {
  categories: StockCategory[]
  showToast: (t: 'success' | 'error', m: string) => void
  /** Set by the item drawer's "View all in History". */
  itemFilter: { id: string; name: string } | null
  onClearItemFilter: () => void
}) {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()

  const [category, setCategory] = useState('')
  const [typeGroup, setTypeGroup] = useState('')
  const [from, setFrom] = useState(() => isoDaysAgo(6))
  const [to, setTo] = useState(() => isoDaysAgo(0))
  const [showVoided, setShowVoided] = useState(false)
  const [total, setTotal] = useState(0)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [exporting, setExporting] = useState(false)

  const pagination = usePagination({ initialPageSize: 20, total })

  const [voidTarget, setVoidTarget] = useState<StockMovement | null>(null)
  const [voidReason, setVoidReason] = useState('')
  const [costTarget, setCostTarget] = useState<StockMovement | null>(null)
  const [costValue, setCostValue] = useState('')
  const [costReason, setCostReason] = useState('')

  const types = TYPE_GROUPS.find(g => g.value === typeGroup)?.types || undefined
  const queryParams = {
    page: pagination.page,
    per_page: pagination.pageSize,
    category_id: category || undefined,
    item_id: itemFilter?.id,
    type: types,
    from: from || undefined,
    to: to || undefined,
    include_voided: showVoided,
    group_sales: true,
  }

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['stockMovements', queryParams],
    queryFn: async () => {
      const res = await apiClient.getStockMovements(queryParams)
      setTotal(res.meta?.total ?? 0)
      return res
    },
    placeholderData: keepPreviousData,
  })
  const rows: StockMovement[] = data?.data ?? []

  const hasNonDefaultFilters = category !== '' || typeGroup !== '' || showVoided || itemFilter != null
  const resetFilters = () => {
    setCategory(''); setTypeGroup(''); setShowVoided(false)
    setFrom(isoDaysAgo(6)); setTo(isoDaysAgo(0))
    onClearItemFilter()
    pagination.goToFirstPage()
  }

  const invalidateAfterLedgerChange = () => {
    qc.invalidateQueries({ queryKey: ['stockMovements'] })
    qc.invalidateQueries({ queryKey: ['stockItems'] })
    qc.invalidateQueries({ queryKey: ['stockSummary'] })
    qc.invalidateQueries({ queryKey: ['advancedStockReport'] })
    qc.invalidateQueries({ queryKey: ['stockAlerts'] })
    qc.invalidateQueries({ queryKey: ['expiringBatches'] })
    qc.invalidateQueries({ queryKey: ['inventoryActivity'] }) // F-FE-N118: void/correct write an activity row
    qc.invalidateQueries({ queryKey: ['stockItem'] }) // and change the drawer's WAC / last cost
  }

  const voidMut = useMutation({
    mutationFn: () => apiClient.voidPurchaseMovement(voidTarget!.id, { reason: voidReason.trim() }),
    onSuccess: res => {
      showToast(res.success ? 'success' : 'error', res.message || 'Purchase voided')
      if (res.success) {
        setVoidTarget(null); setVoidReason('')
        invalidateAfterLedgerChange()
      }
    },
    onError: (err: Error) => showToast('error', err.message || 'Void failed'),
  })
  const correctMut = useMutation({
    mutationFn: () => apiClient.correctPurchaseMovementCost(costTarget!.id, { unit_cost: Number(costValue), reason: costReason.trim() }),
    onSuccess: res => {
      showToast(res.success ? 'success' : 'error', res.message || 'Cost updated')
      if (res.success) {
        setCostTarget(null); setCostValue(''); setCostReason('')
        invalidateAfterLedgerChange()
      }
    },
    onError: (err: Error) => showToast('error', err.message || 'Correction failed'),
  })

  const handleExport = async () => {
    setExporting(true)
    try {
      await apiClient.exportInventoryReportsXlsx(from || isoDaysAgo(6), to || isoDaysAgo(0), 'movements')
    } catch (e) {
      showToast('error', e instanceof Error && e.message ? e.message : 'Export failed')
    } finally {
      setExporting(false)
    }
  }

  const toggleExpanded = (id: string) =>
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const filterSelectCls = 'border rounded-md px-3 py-2 text-sm bg-background'

  const columnCount = 6
  const rangeLabel = useMemo(() => (from || to) ? undefined : 'all time', [from, to])

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <select aria-label="Filter by what happened" value={typeGroup} onChange={e => { setTypeGroup(e.target.value); pagination.goToFirstPage() }} className={filterSelectCls}>
          {TYPE_GROUPS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
        </select>
        <select aria-label="Filter by category" value={category} onChange={e => { setCategory(e.target.value); pagination.goToFirstPage() }} className={`${filterSelectCls} min-w-[140px]`}>
          <option value="">All categories</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <DatePicker className="w-[170px]" value={from} onChange={v => { setFrom(v); pagination.goToFirstPage() }} max={to || undefined} />
        <span className="text-sm text-muted-foreground">to</span>
        <DatePicker className="w-[170px]" value={to} onChange={v => { setTo(v); pagination.goToFirstPage() }} min={from || undefined} />
        <label className="flex items-center gap-1.5 text-sm text-muted-foreground cursor-pointer select-none">
          <input type="checkbox" checked={showVoided} onChange={e => { setShowVoided(e.target.checked); pagination.goToFirstPage() }} />
          Show voided
        </label>
        {itemFilter && (
          <Badge variant="outline" className="text-xs gap-1">
            Item: {itemFilter.name}
            <button type="button" aria-label="Clear item filter" onClick={() => { onClearItemFilter(); pagination.goToFirstPage() }}>
              <X className="w-3 h-3" />
            </button>
          </Badge>
        )}
        {hasNonDefaultFilters && (
          <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={resetFilters}>Clear filters</Button>
        )}
        <div className="ml-auto">
          <Button variant="outline" size="sm" className="h-8" disabled={exporting} onClick={handleExport}>
            <Download className="w-3.5 h-3.5 mr-1.5" /> {exporting ? 'Exporting…' : 'Export'}
          </Button>
        </div>
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full min-w-[820px] text-sm">
          <thead className="bg-muted/50">
            <tr className="text-xs text-muted-foreground">
              <th scope="col" className="px-4 py-2.5 text-left font-medium">When</th>
              <th scope="col" className="px-4 py-2.5 text-left font-medium">What happened</th>
              <th scope="col" className="px-4 py-2.5 text-left font-medium">Item</th>
              <th scope="col" className="px-4 py-2.5 text-right font-medium">Qty</th>
              <th scope="col" className="px-4 py-2.5 text-right font-medium">Value</th>
              <th scope="col" className="px-4 py-2.5 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading && rows.length === 0 && (
              <tr><td colSpan={columnCount} className="px-4 py-10 text-center text-muted-foreground">Loading history…</td></tr>
            )}
            {isError && (
              <tr><td colSpan={columnCount} className="px-4 py-10 text-center text-danger-fg">
                {(error as Error)?.message || 'Could not load history'}
              </td></tr>
            )}
            {!isLoading && !isError && rows.length === 0 && (
              <tr><td colSpan={columnCount} className="px-4 py-10 text-center text-muted-foreground">
                Nothing recorded {rangeLabel ?? 'in this window'}{hasNonDefaultFilters ? ' with these filters' : ''}.
                {hasNonDefaultFilters && <> <button type="button" className="underline" onClick={resetFilters}>Clear filters</button></>}
              </td></tr>
            )}
            {rows.map(m => {
              const d = describeMovement(m)
              const voided = Boolean(m.voided_at)
              const grouped = (m.line_count ?? 1) > 1
              const isOpen = expanded.has(m.id)
              const canFix = m.movement_type === 'purchase' && m.purchase_can_void && !voided
              return (
                <>
                  <tr key={m.id} className={voided ? 'opacity-60' : 'hover:bg-muted/30'}>
                    <td className="px-4 py-2.5 whitespace-nowrap text-muted-foreground">{formatDateTimeHuman(m.created_at)}</td>
                    <td className="px-4 py-2.5">
                      <div className={`flex items-center gap-1.5 font-medium ${voided ? 'line-through' : ''} ${MOVEMENT_TONE_CLS[d.tone]}`}>
                        {grouped && (
                          <button type="button" aria-label={isOpen ? 'Collapse' : 'Expand'} aria-expanded={isOpen} onClick={() => toggleExpanded(m.id)} className="text-muted-foreground">
                            {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                          </button>
                        )}
                        <span className="truncate">{d.title}</span>
                        {m.movement_type === 'purchase' && m.purchase_order_id && (
                          <Badge variant="outline" className="text-[10px] shrink-0">PO {m.purchase_order_id.slice(0, 8)}</Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {m.created_by_name ?? (m.movement_type === 'opening_balance' ? 'System' : '')}
                        {voided && (
                          <span> · Voided{m.voided_by_name ? ` by ${m.voided_by_name}` : ''}{m.void_reason ? `: ${m.void_reason}` : ''}</span>
                        )}
                        {!voided && d.detail ? ` · ${d.detail}` : ''}
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      {grouped
                        ? <span className="text-muted-foreground">{m.line_count} items</span>
                        : <span className={voided ? 'line-through' : ''}>{m.item_name}</span>}
                    </td>
                    <td
                      className={`px-4 py-2.5 text-right tabular-nums ${voided ? 'line-through' : ''}`}
                      title={!grouped && isNearIntegerArtifact(m.quantity) ? QTY_ARTIFACT_HINT : undefined}
                    >
                      {grouped ? '—' : <>{m.quantity > 0 ? '+' : ''}{fmtQtyUnit(m.quantity, m.item_unit ?? '')}</>}
                    </td>
                    <td className={`px-4 py-2.5 text-right tabular-nums ${voided ? 'line-through' : ''}`}
                      title="Inventory value moved, at the item's average cost. Order profit uses the COGS snapshot on the order itself.">
                      {m.total_cost != null ? formatCurrency(m.total_cost) : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      {canFix && (
                        <div className="inline-flex gap-1.5">
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setCostTarget(m); setCostValue(m.unit_cost != null ? String(m.unit_cost) : '') }}>
                            Cost
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs text-danger-fg" onClick={() => setVoidTarget(m)}>
                            Void
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                  {grouped && isOpen && (
                    <tr key={`${m.id}-lines`} className="bg-muted/20">
                      <td colSpan={columnCount} className="px-4 py-2">
                        <ul className="space-y-1 text-xs text-muted-foreground">
                          {(m.lines ?? []).map((l, i) => (
                            <li key={i} className="flex justify-between gap-4">
                              <span>{l.item_name}</span>
                              <span className="tabular-nums" title={isNearIntegerArtifact(l.quantity) ? QTY_ARTIFACT_HINT : undefined}>
                                {l.quantity > 0 ? '+' : ''}{fmtQtyUnit(l.quantity, l.unit)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  )}
                </>
              )
            })}
          </tbody>
        </table>
      </div>

      <PaginationControlsComponent pagination={pagination} total={total} />

      {/* Void dialog */}
      <Dialog open={voidTarget != null} onOpenChange={o => { if (!o) { setVoidTarget(null); setVoidReason('') } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Void purchase — {voidTarget?.item_name}</DialogTitle>
            <DialogDescription>
              Removes {voidTarget ? fmtQty(Math.abs(voidTarget.quantity)) : ''} {voidTarget?.item_unit} from stock, deletes its cost layer,
              re-computes the item&apos;s average cost, and removes the linked expense. Only possible while nothing
              from this purchase has been used.
            </DialogDescription>
          </DialogHeader>
          <Input placeholder="Reason (required)" value={voidReason} onChange={e => setVoidReason(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setVoidTarget(null); setVoidReason('') }}>Cancel</Button>
            <Button variant="destructive" disabled={voidReason.trim() === '' || voidMut.isPending} onClick={() => voidMut.mutate()}>
              {voidMut.isPending ? 'Voiding…' : 'Void purchase'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cost-correction dialog */}
      <Dialog open={costTarget != null} onOpenChange={o => { if (!o) { setCostTarget(null); setCostValue(''); setCostReason('') } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Correct cost — {costTarget?.item_name}</DialogTitle>
            <DialogDescription>
              New price per {costTarget?.item_unit}. Updates this purchase, its cost layer, and the item&apos;s
              average cost (which future sales are costed at). 0 is allowed for free stock.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Input type="number" step="0.01" min="0" placeholder={`Cost per ${costTarget?.item_unit ?? 'unit'}`} value={costValue} onChange={e => setCostValue(e.target.value)} />
            <Input placeholder="Reason (required)" value={costReason} onChange={e => setCostReason(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCostTarget(null); setCostValue(''); setCostReason('') }}>Cancel</Button>
            <Button
              disabled={costReason.trim() === '' || costValue.trim() === '' || Number.isNaN(Number(costValue)) || Number(costValue) < 0 || correctMut.isPending}
              onClick={() => correctMut.mutate()}
            >
              {correctMut.isPending ? 'Saving…' : 'Save cost'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
